import { useLayoutEffect, useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { gsap } from 'gsap';
import { GoArrowUpRight } from 'react-icons/go';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import './CardNav.css';

const CardNav = ({
  logo,
  logoAlt = 'Logo',
  items,
  className = '',
  ease = 'power3.out',
  baseColor = '#fff',
  menuColor,
  buttonBgColor,
  buttonTextColor,
  buttonText = 'Start Game',
  buttonVariant = 'default', // 'default' | 'hero' - hero matches Teams that play together card
  onButtonClick,
  showButton = true,
  showBackButton = false,
  onBackClick,
  backButtonText = '← Back',
  showHamburger = true,
  logoText = 'The Creative Coaster',
  showDraftBadge = false,
  user = null,
  userProfile = null,
  onSignOut = null,
  onSignInClick = null,
  onProfileClick = null
}) => {
  const [isHamburgerOpen, setIsHamburgerOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const navRef = useRef(null);
  const cardsRef = useRef([]);
  const tlRef = useRef(null);
  const userMenuRef = useRef(null);
  const avatarButtonRef = useRef(null);
  const isAnimatingRef = useRef(false); // Track if animation is in progress

  // Get user initials from email or name
  const getUserInitials = () => {
    if (!user) return '?';
    const email = user.email || '';
    const name = userProfile?.full_name || '';
    
    if (name) {
      const parts = name.trim().split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return name[0].toUpperCase();
    }
    
    if (email) {
      return email[0].toUpperCase();
    }
    
    return '?';
  };

  // Get profile image URL
  const getProfileImageUrl = () => {
    if (userProfile?.avatar_url) {
      return userProfile.avatar_url;
    }
    return null;
  };

  // Calculate dropdown position and render it via portal
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });

  useEffect(() => {
    if (showUserMenu && avatarButtonRef.current) {
      const updatePosition = () => {
        const rect = avatarButtonRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 8, // 0.5rem = 8px
          right: window.innerWidth - rect.right
        });
      };
      
      updatePosition();
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition, true);
      
      return () => {
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', updatePosition, true);
      };
    }
  }, [showUserMenu]);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedInsideAvatar = userMenuRef.current?.contains(event.target);
      const clickedInsideDropdown = userMenuRef.current?.dropdownElement?.contains(event.target);
      
      if (!clickedInsideAvatar && !clickedInsideDropdown) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      // Use a small delay to avoid closing immediately when opening
      setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 0);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  // Render dropdown menu via portal
  const renderDropdown = () => {
    if (!showUserMenu || !user) return null;

    const dropdownContent = (
      <div 
        ref={(el) => {
          if (el && userMenuRef.current) {
            userMenuRef.current.dropdownElement = el;
          }
        }}
        style={{
          position: 'fixed',
          top: `${dropdownPosition.top}px`,
          right: `${dropdownPosition.right}px`,
          background: baseColor,
          border: `1px solid ${menuColor || 'rgba(255, 255, 255, 0.2)'}`,
          borderRadius: '8px',
          padding: '0.5rem',
          minWidth: '200px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
          zIndex: 10000
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{
          padding: '0.75rem',
          borderBottom: `1px solid ${menuColor || 'rgba(255, 255, 255, 0.1)'}`,
          marginBottom: '0.5rem'
        }}>
          <div style={{
            color: menuColor || '#fff',
            fontSize: '0.875rem',
            fontWeight: 600,
            marginBottom: '0.25rem'
          }}>
            {userProfile?.full_name || 'User'}
          </div>
          <div style={{
            color: menuColor || 'rgba(255, 255, 255, 0.7)',
            fontSize: '0.75rem'
          }}>
            {user.email}
          </div>
          {userProfile?.role && (
            <div style={{
              color: buttonBgColor || '#D702F6',
              fontSize: '0.75rem',
              marginTop: '0.25rem',
              fontWeight: 500
            }}>
              {userProfile.role === 'host' ? 'Host' : 'Player'}
            </div>
          )}
        </div>
                    {onSignOut && (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            setShowUserMenu(false);
                            if (onProfileClick) {
                              onProfileClick();
                            }
                          }}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            background: 'transparent',
                            border: 'none',
                            color: menuColor || '#fff',
                            textAlign: 'left',
                            cursor: 'pointer',
                            borderRadius: '4px',
                            fontSize: '0.875rem',
                            transition: 'background 0.2s ease',
                            marginBottom: '0.25rem'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = 'transparent';
                          }}
                        >
                          Profile
                        </button>
                        <div style={{
                          height: '1px',
                          background: menuColor || 'rgba(255, 255, 255, 0.1)',
                          margin: '0.5rem 0'
                        }} />
                        <button
                          type="button"
                          onClick={async () => {
                            setShowUserMenu(false);
                            if (onSignOut) {
                              await onSignOut();
                            }
                          }}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            background: 'transparent',
                            border: 'none',
                            color: menuColor || '#fff',
                            textAlign: 'left',
                            cursor: 'pointer',
                            borderRadius: '4px',
                            fontSize: '0.875rem',
                            transition: 'background 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = 'transparent';
                          }}
                        >
                          Sign Out
                        </button>
                      </>
                    )}
      </div>
    );

    return createPortal(dropdownContent, document.body);
  };

  const calculateHeight = () => {
    const navEl = navRef.current;
    if (!navEl) return 260;

    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    if (isMobile) {
      const contentEl = navEl.querySelector('.card-nav-content');
      if (contentEl) {
        const wasVisible = contentEl.style.visibility;
        const wasPointerEvents = contentEl.style.pointerEvents;
        const wasPosition = contentEl.style.position;
        const wasHeight = contentEl.style.height;

        contentEl.style.visibility = 'visible';
        contentEl.style.pointerEvents = 'auto';
        contentEl.style.position = 'static';
        contentEl.style.height = 'auto';
        void contentEl.offsetHeight; // Force reflow

        const topBar = 60;
        const padding = 16;
        const contentHeight = contentEl.scrollHeight;

        contentEl.style.visibility = wasVisible;
        contentEl.style.pointerEvents = wasPointerEvents;
        contentEl.style.position = wasPosition;
        contentEl.style.height = wasHeight;

        return topBar + contentHeight + padding;
      }
    }
    return 260;
  };

  const createTimeline = () => {
    const navEl = navRef.current;
    if (!navEl) return null;

    gsap.set(navEl, { height: 60, overflow: 'hidden' });
    gsap.set(cardsRef.current, { y: 50, opacity: 0 });

    const tl = gsap.timeline({ paused: true });
    tl.to(navEl, {
      height: calculateHeight,
      duration: 0.4,
      ease
    });
    tl.to(cardsRef.current, { y: 0, opacity: 1, duration: 0.4, ease, stagger: 0.08 }, '-=0.1');

    return tl;
  };

  useLayoutEffect(() => {
    // Don't recreate timeline if animation is in progress
    if (isAnimatingRef.current) {
      console.warn('[CardNav] Timeline recreation blocked - animation in progress');
      return;
    }

    const tl = createTimeline();
    if (tl) {
      // If menu was expanded, set timeline to expanded state
      if (isExpanded) {
        tl.progress(1);
      }
      tlRef.current = tl;
    }

    return () => {
      const currentTl = tlRef.current;
      if (currentTl && !currentTl.killed) {
        currentTl.kill();
      }
      tlRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ease, items]);

  useLayoutEffect(() => {
    const handleResize = () => {
      if (!tlRef.current) return;

      if (isExpanded) {
        const newHeight = calculateHeight();
        gsap.set(navRef.current, { height: newHeight });
        tlRef.current.kill();
        const newTl = createTimeline();
        if (newTl) {
          newTl.progress(1);
          tlRef.current = newTl;
        }
      } else {
        tlRef.current.kill();
        const newTl = createTimeline();
        if (newTl) {
          tlRef.current = newTl;
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded]);

  const toggleMenu = () => {
    // Prevent toggling during animation to avoid state desync
    if (isAnimatingRef.current) {
      console.warn('[CardNav] Toggle blocked - animation in progress');
      return;
    }

    const tl = tlRef.current;
    if (!tl) {
      console.warn('[CardNav] Timeline not available, resetting state');
      // Reset state if timeline is missing
      setIsHamburgerOpen(false);
      setIsExpanded(false);
      return;
    }

    // Check if timeline is valid (not killed)
    if (tl.killed) {
      console.warn('[CardNav] Timeline was killed, recreating');
      // Timeline was killed, need to recreate
      const newTl = createTimeline();
      if (newTl) {
        tlRef.current = newTl;
        // Reset state and try again
        setIsHamburgerOpen(false);
        setIsExpanded(false);
        return;
      }
    }

    if (!isExpanded) {
      isAnimatingRef.current = true;
      setIsHamburgerOpen(true);
      setIsExpanded(true);
      tl.play(0);
      // Reset animation flag after animation completes
      tl.eventCallback('onComplete', () => {
        isAnimatingRef.current = false;
      });
    } else {
      isAnimatingRef.current = true;
      setIsHamburgerOpen(false);
      tl.eventCallback('onReverseComplete', () => {
        setIsExpanded(false);
        isAnimatingRef.current = false;
      });
      tl.reverse();
    }
  };

  const setCardRef = i => el => {
    if (el) cardsRef.current[i] = el;
  };

  return (
    <>
      <div className={`card-nav-container ${className}`}>
        <nav ref={navRef} className={`card-nav ${isExpanded ? 'open' : ''}`} style={{ backgroundColor: baseColor }}>
        <div className="card-nav-top">
          <div className="card-nav-left-group">
            {showBackButton && (
              <button
                type="button"
                className="card-nav-back-button"
                onClick={onBackClick}
                style={{
                  color: menuColor || '#fff',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.5rem 1rem',
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  transition: 'opacity 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.opacity = '0.7';
                }}
                onMouseLeave={(e) => {
                  e.target.style.opacity = '1';
                }}
              >
                {backButtonText}
              </button>
            )}
            {showHamburger && (
              <div
                className={`hamburger-menu ${isHamburgerOpen ? 'open' : ''}`}
                onClick={toggleMenu}
                role="button"
                aria-label={isExpanded ? 'Close menu' : 'Open menu'}
                tabIndex={0}
                style={{ color: menuColor || '#fff' }}
              >
                <div className="hamburger-line" />
                <div className="hamburger-line" />
              </div>
            )}
          </div>
          <div className="logo-container">
            <img src={logo} alt={logoAlt} className="logo" />
            <span className="logo-text" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              {logoText}
              {showDraftBadge && (
                <div style={{
                  display: 'inline-block',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '12px',
                  background: 'rgba(255, 193, 7, 0.2)',
                  color: '#FFC107',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  border: '1px solid rgba(255, 193, 7, 0.3)'
                }}>
                  Draft
                </div>
              )}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            {showButton && (
              <button
                type="button"
                className={`card-nav-cta-button ${buttonVariant === 'hero' ? 'card-nav-cta-button--hero' : ''}`}
                style={buttonVariant === 'hero' ? {
                  background: 'linear-gradient(120deg, #D702F6 0%, #8C2BFF 100%)',
                  border: 'none',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  boxShadow: '0 10px 25px rgba(215, 2, 246, 0.45)',
                  padding: '0.7em 1.5em',
                  borderRadius: '12px',
                  fontSize: '0.8em',
                  fontWeight: 700,
                  fontFamily: "'Orbitron', sans-serif",
                  cursor: 'pointer',
                  transition: 'transform 0.25s ease, box-shadow 0.25s ease'
                } : {
                  backgroundColor: buttonBgColor,
                  border: `2px solid ${buttonBgColor}`,
                  color: buttonTextColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  boxShadow: '0 4px 15px rgba(215, 2, 246, 0.4)',
                  padding: '0.875rem 2rem',
                  borderRadius: '8px',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  if (buttonVariant === 'hero') {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 14px 32px rgba(215,2,246,0.6)';
                  } else {
                    e.target.style.backgroundColor = buttonBgColor;
                    e.target.style.color = buttonTextColor;
                    e.target.style.boxShadow = '0 6px 20px rgba(215, 2, 246, 0.6)';
                    e.target.style.transform = 'translateY(-1px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (buttonVariant === 'hero') {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 10px 25px rgba(215, 2, 246, 0.45)';
                  } else {
                    e.target.style.backgroundColor = buttonBgColor;
                    e.target.style.color = buttonTextColor;
                    e.target.style.boxShadow = '0 4px 15px rgba(215, 2, 246, 0.4)';
                    e.target.style.transform = 'translateY(0)';
                  }
                }}
                onClick={onButtonClick}
              >
                {buttonText}
              </button>
            )}
            
            {/* User Avatar/Menu */}
            {user ? (
              <div ref={userMenuRef} style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <button
                  ref={avatarButtonRef}
                  type="button"
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '40px',
                    height: '40px',
                    transition: 'transform 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  aria-label="User menu"
                >
                  <Avatar 
                    style={{
                      width: '40px',
                      height: '40px',
                      minWidth: '40px',
                      minHeight: '40px',
                      flexShrink: 0,
                      border: '2px solid white',
                      borderRadius: '50%',
                      overflow: 'hidden'
                    }}
                  >
                    {getProfileImageUrl() && (
                      <AvatarImage 
                        src={getProfileImageUrl()} 
                        alt={userProfile?.full_name || user.email || 'User'}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    )}
                    <AvatarFallback 
                      style={{
                        backgroundColor: '#D702F6',
                        width: '100%',
                        height: '100%',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '14px',
                        fontWeight: 600,
                        lineHeight: '1'
                      }}
                    >
                      {getUserInitials()}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </div>
            ) : (
              onSignInClick && (
                <button
                  type="button"
                  className="card-nav-signup-button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (onSignInClick) {
                      onSignInClick();
                    }
                  }}
                  style={{
                    background: 'transparent',
                    border: `2px solid ${buttonBgColor || '#D702F6'}`,
                    color: buttonBgColor || '#D702F6',
                    padding: '0.5rem 1rem',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = buttonBgColor || '#D702F6';
                    e.target.style.color = buttonTextColor || '#fff';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = 'transparent';
                    e.target.style.color = buttonBgColor || '#D702F6';
                  }}
                >
                  Sign In
                </button>
              )
            )}
          </div>
        </div>
        <div className="card-nav-content" aria-hidden={!isExpanded}>
          {(items || []).slice(0, 3).map((item, idx) => (
            <div
              key={`${item.label}-${idx}`}
              className="nav-card"
              ref={setCardRef(idx)}
              style={{ 
                color: item.textColor || '#ffffff',
                background: item.bgColor 
                  ? `linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, ${item.bgColor}20 100%)`
                  : 'rgba(255, 255, 255, 0.05)'
              }}
            >
              <div className="nav-card-label">{item.label}</div>
              <div className="nav-card-links">
                {item.links?.map((lnk, i) => (
                  <a 
                    key={`${lnk.label}-${i}`} 
                    className="nav-card-link" 
                    href={lnk.href} 
                    onClick={lnk.onClick}
                    aria-label={lnk.ariaLabel}
                  >
                    <GoArrowUpRight className="nav-card-link-icon" aria-hidden="true" />
                    {lnk.label}
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>
      </nav>
      </div>
      {renderDropdown()}
    </>
  );
};

export default CardNav;

